/**
 * NOTE on redux: `customerPreferences` and `couponRangeList` are new modules
 * built specifically for this screen (and shared with SignUp/PreferencesForm
 * for the range list) — neither existed anywhere before. Endpoints are best
 * guesses following existing naming conventions; verify against the real
 * backend. See modules/customerPreferences and modules/couponRangeList.
 *
 * `FormFields` removed earlier: replaced with raw snake_case strings
 * (coupon_search_range, categories) matching customerPreferences.preferences.
 *
 * TEMPORARY: `formMessages` (from SignUp/PreferencesForm/messages) removed
 * since that file doesn't exist yet either. The three usages are stubbed:
 *   - entityCategories / couponRangeListKey were message descriptors passed
 *     into getNormalizedEntity / getFormattedLoadingError. Per Dashboard.js's
 *     real usage of getNormalizedEntity, this util calls
 *     intl.formatMessage(descriptor) internally, which requires an object
 *     with an `id` (a bare string would throw) — stubbed with inline
 *     { id, defaultMessage } objects below, not plain strings.
 *   - the "Categories" section heading was a <FormattedMessage>, swapped
 *     for plain text (no formatMessage involved there, so a plain string is
 *     fine).
 * Swap these back to real i18n message descriptors once
 * SignUp/PreferencesForm/messages.js (or wherever these should live) exists
 * — search for "STUB:" below to find every spot.
 */
import React, { useCallback, useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import { Controller,useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Spinner,UndrawTravelPlansIcon } from 'components';
import { actions as categoriesActions, duck } from 'coupon-common/src/modules/businessCategories';
import { actions as couponRangeListActions } from 'coupon-common/src/modules/couponRangeList';
import { actions as preferencesActions } from 'coupon-common/src/modules/customerPreferences';
import {
  dispatchAsAsync,
  getFormattedLoadingError,
  getNormalizedEntity,
  getObjectsDiff,
} from 'coupon-common/src/utils';

import messages from './messages';
import styles from './Preferences.module.scss';

const categoriesKey = duck.businessCategoriesEntity.key;

// Field names on customerPreferences.preferences (see modules/customerPreferences/duck.js).
const FIELD_COUPON_SEARCH_RANGE = 'coupon_search_range';
const FIELD_CATEGORIES = 'categories';

const Preferences = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const businessCategories = useSelector(state => state.businessCategories);
  const couponRangeList = useSelector(state => state.couponRangeList);
  const customerPreferences = useSelector(state => state.customerPreferences);

  const [toast, setToast] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({ defaultValues: customerPreferences.preferences });

  const getFormData = useCallback(() => {
    dispatch(categoriesActions.getBusinessCategories());
    dispatch(couponRangeListActions.getCouponRangeList());
  }, [dispatch]);

  const getPreferencesData = useCallback(() => {
    dispatch(preferencesActions.getPreferences());
  }, [dispatch]);

  useEffect(() => {
    getFormData();
    getPreferencesData();
  }, [getFormData, getPreferencesData]);

  // STUB: was formMessages.entityCategories — inline message descriptor
  // object until real i18n messages exist (getNormalizedEntity calls
  // intl.formatMessage on this, which needs an `id`, not a bare string).
  const categoriesNormalized = getNormalizedEntity(businessCategories, categoriesKey, intl, {
    id: 'cb.screens.Preferences.entityCategories',
    defaultMessage: 'business categories',
  });

  const { getPreferences } = customerPreferences;

  const handleSave = async formValues => {
    const updatedValues = getObjectsDiff(customerPreferences.preferences, formValues);

    if (Object.keys(updatedValues).length > 0) {
      const errors = await dispatchAsAsync(preferencesActions.updatePreferences(formValues), dispatch);

      if (errors) {
        setToast(intl.formatMessage(messages.couldNotUpdatePreferencesError));
        setTimeout(() => setToast(null), 3000);
      }
    }
  };

  // STUB: was formMessages.couponRangeListKey — inline message descriptor
  // object until real i18n messages exist (same reasoning as above).
  const formattedCouponRangeError = getFormattedLoadingError(intl, couponRangeList.error, {
    id: 'cb.screens.Preferences.couponRangeListKey',
    defaultMessage: 'coupon range list',
  });

  if (getPreferences.error) {
    return (
      <div styleName="page">
        <p styleName="state-message">Something went wrong loading your preferences.</p>
      </div>
    );
  }

  if (getPreferences.fetching && !getPreferences.fetched) {
    return (
      <div styleName="page" style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner show size="lg" />
      </div>
    );
  }

  return (
    <div styleName="page">
      <div styleName="card">
        <h1 styleName="title">Preferences</h1>
        <div styleName="image-wrap">
          <UndrawTravelPlansIcon />
        </div>

        <label styleName="field-label" htmlFor="prefs-range">
          <FormattedMessage {...messages.selectDistance} />
        </label>
        <Controller
          control={control}
          name={FIELD_COUPON_SEARCH_RANGE}
          render={({ field: { onChange, value } }) => (
            <select
              id="prefs-range"
              styleName="select"
              value={value ?? customerPreferences.preferences.coupon_search_range ?? ''}
              onChange={e => onChange(Number(e.target.value))}
              disabled={!!couponRangeList.fetching || !!formattedCouponRangeError}
            >
              <option value="" disabled>
                Select a range
              </option>
              {couponRangeList.options.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        />
        {formattedCouponRangeError && <p styleName="feedback">{formattedCouponRangeError}</p>}

        {/* STUB: was <FormattedMessage {...formMessages.categories} /> */}
        <h2 styleName="categories-title">Categories</h2>
        <Controller
          control={control}
          name={FIELD_CATEGORIES}
          render={({ field: { onChange, value } }) => (
            <div styleName="tag-cloud">
              {categoriesNormalized.isFetching && <Spinner show size="sm" />}
              {(categoriesNormalized.entityList || []).map(cat => {
                const selected = (value || []).includes(cat.id);
                return (
                  <button
                    type="button"
                    key={cat.id}
                    styleName={selected ? 'tag tag-selected' : 'tag'}
                    onClick={() =>
                      onChange(selected ? value.filter(id => id !== cat.id) : [...(value || []), cat.id])
                    }
                  >
                    {cat.name}
                  </button>
                );
              })}
            </div>
          )}
        />

        <div styleName="submit-row">
          <button type="button" styleName="save-btn" onClick={handleSubmit(handleSave)} disabled={isSubmitting}>
            {isSubmitting ? <Spinner show size="sm" /> : intl.formatMessage(messages.saveBtn)}
          </button>
        </div>
      </div>

      {toast && <div styleName="toast">{toast}</div>}
    </div>
  );
};

export default CSSModule(Preferences, styles, { allowMultiple: true });